import * as React from 'react';

import { scrollIntoView } from '../../../../lib/dom';

export interface UseTouchSelectNavigationProps {
  isMenuVisible: boolean;
  setIsMenuVisible: (visible: boolean) => void;
  filteredOptions: Array<{
    id: string | number | undefined;
    label: React.ReactNode;
    isDisabled?: boolean;
  }>;
  currentFocusIndex: number;
  setCurrentFocusIndex: React.Dispatch<React.SetStateAction<number>>;
  setIsKeyboardNavigating: React.Dispatch<React.SetStateAction<boolean>>;
  handleNewSelection: () => void;
  triggerRef: React.RefObject<HTMLDivElement | null>;
  optionsContainerRef: React.RefObject<HTMLDivElement | null>;
  lastMousePositionRef: { current: { x: number; y: number } };
}

export const useTouchSelectNavigation = ({
  isMenuVisible,
  setIsMenuVisible,
  filteredOptions,
  currentFocusIndex,
  setCurrentFocusIndex,
  setIsKeyboardNavigating,
  handleNewSelection,
  triggerRef,
  optionsContainerRef,
  lastMousePositionRef,
}: UseTouchSelectNavigationProps) => {
  const scrollToOption = React.useCallback(
    (index: number, block?: ScrollLogicalPosition) => {
      const options = optionsContainerRef.current?.querySelectorAll(
        '[data-type-id="re-ui-select-option"]',
      );
      const optionElement = options?.[index] as HTMLElement | undefined;

      if (optionElement) {
        requestAnimationFrame(() =>
          scrollIntoView({ element: optionElement, block }),
        );
      }
    },
    [optionsContainerRef],
  );

  const detectMouseMovement = React.useCallback(
    (e: MouseEvent) => {
      const { x, y } = lastMousePositionRef.current;
      const threshold = 5; // pixels

      if (
        Math.abs(e.clientX - x) > threshold ||
        Math.abs(e.clientY - y) > threshold
      ) {
        setIsKeyboardNavigating(false);
        lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
      }
    },
    [lastMousePositionRef, setIsKeyboardNavigating],
  );

  const handleSearchKeydown = React.useCallback<React.KeyboardEventHandler>(
    (e) => {
      const focusable = triggerRef.current as HTMLDivElement;
      switch (e.key) {
        case 'ArrowDown': {
          setIsKeyboardNavigating(true);
          if (isMenuVisible) {
            e.preventDefault();
            setCurrentFocusIndex((i) => {
              const newFocusIndex = Math.min(filteredOptions.length - 1, i + 1);

              scrollToOption(newFocusIndex);

              return newFocusIndex;
            });
          } else {
            setIsMenuVisible(true);
          }
          break;
        }

        case 'ArrowUp': {
          setIsKeyboardNavigating(true);
          if (isMenuVisible) {
            e.preventDefault();
            setCurrentFocusIndex((i) => {
              const newFocusIndex = Math.max(0, i - 1);

              scrollToOption(newFocusIndex);

              return newFocusIndex;
            });
          } else {
            setIsMenuVisible(true);
          }
          break;
        }

        case 'Escape':
          e.preventDefault();
          setIsMenuVisible(false);
          setTimeout(() => focusable?.focus());
          break;

        case 'Enter':
          e.preventDefault();
          if (
            isMenuVisible &&
            !filteredOptions[currentFocusIndex]?.isDisabled
          ) {
            handleNewSelection();
          }
          break;

        case 'Tab':
          setIsMenuVisible(false);
          break;

        case 'Backspace':
        case 'ArrowLeft':
        case 'ArrowRight':
          break;

        default:
          if (!isMenuVisible) {
            setIsMenuVisible(true);
          }
          break;
      }
    },
    [
      triggerRef,
      setIsKeyboardNavigating,
      isMenuVisible,
      setIsMenuVisible,
      setCurrentFocusIndex,
      filteredOptions,
      currentFocusIndex,
      handleNewSelection,
      scrollToOption,
    ],
  );

  /**
   * Set up mouse movement detection to re-enable mouse focusing after keyboard navigation
   */
  React.useEffect(() => {
    if (isMenuVisible) {
      document.addEventListener('mousemove', detectMouseMovement);
      return () => {
        document.removeEventListener('mousemove', detectMouseMovement);
      };
    }
  }, [isMenuVisible, detectMouseMovement]);

  return {
    handleSearchKeydown,
    scrollToOption,
  };
};
